import { Fragment, lazy, Suspense, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getTabStatus, TABS_LABELS, TABS_URLS } from "./helpers";
import { ApplicationFooter } from "./ApplicationFooter";
import { useHideMenusInFullscreen } from "./hooks/useHideMenusInFullscreen";
import { metricsBus } from "@/common/buses";
import { useAppStore } from "@/common/store/appStore";
import { useUserStore } from "@/common/store/userStore";
import { useLessNavigation } from "@/common/hooks/useLessNavigation";
import { isAuthenticated } from "@/common/services/Auth";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";

const ProfileMenu = lazy(() => import("@/components/profile-menu/ProfileMenu"));

const ApplicationFooterContainer = () => {
  const currentUserName = useUserStore((state) => state.currentUser?.username) || null;
  const isProfileMenuOpen = useAppStore((state) => state.isProfileMenuOpen);
  const toggleProfileMenu = useAppStore((state) => state.toggleProfileMenu);
  const hideAllMenus = useAppStore((state) => state.hideAllMenus);
  const { pathname } = useLocation();
  const { canShow } = useLessNavigation();
  const { navigateSignIn } = useAuthNavigate();

  useHideMenusInFullscreen();

  const tabStatuses = useMemo(() => getTabStatus(pathname, currentUserName), [pathname, currentUserName]);

  const sendMetrics = (name: string, label: string, targetURL: string) => {
    metricsBus.emit("menuButtonClicked", { name, label });
    metricsBus.emit("watchPageNavigation", { elementType: "Navbar Item", targetURL });
  };

  const isTabActive = (tab: string) => tabStatuses[tab];

  const onProfileClick = useCallback(async () => {
    if (!(await isAuthenticated())) {
      navigateSignIn();
      return;
    }

    toggleProfileMenu();
    sendMetrics(TABS_URLS.profile, TABS_LABELS.profile, "/profile");
  },[currentUserName, toggleProfileMenu]);

  const onSendMetricAndCloseMenu = (tabName: string, label: string, targetURL: string) => {
    hideAllMenus();
    sendMetrics(tabName, label, targetURL);
  };

  if (!canShow) {
    return null;
  }

  return (
    <Fragment>
      <ApplicationFooter
        currentUserName={currentUserName}
        isTabActive={isTabActive}
        onProfileClick={onProfileClick}
        onSendMetricAndCloseMenu={onSendMetricAndCloseMenu}
      />
      {isProfileMenuOpen && <Suspense><ProfileMenu /></Suspense>}
    </Fragment>
  );
};

export default ApplicationFooterContainer;
